import { z } from 'zod';
import { ComponentType } from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import { DevelopmentTool } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { AppGroup } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';

export const createAppSchema = z.object({
  app: z.object({
    id: z.string(),
  }),
});

export const createOAuthAppSchema = z.object({
  oAuthApp: z.object({
    id: z.string(),
  }),
});

const topologySchema = z
  .object({
    compId: z.string(),
    compName: z.string(),
    compType: z.literal(ComponentType.TOPOLOGY),
    compData: z.object({
      topology: z.object({
        topology: z.object({
          backendApiUrl: z.string().min(1),
        }),
      }),
    }),
  })
  .passthrough();

export type TopologyComponent = z.TypeOf<typeof topologySchema>;

const componentsSchema = z.array(
  z.discriminatedUnion('compType', [
    topologySchema,
    ...Object.values(ComponentType)
      .filter((type) => type !== ComponentType.TOPOLOGY)
      .map((type) => {
        return z
          .object({
            compId: z.string(),
            compName: z.string().optional(),
            compData: z.unknown(),
            compType: z.literal(type),
          })
          .passthrough();
      }),
  ])
);

export type AppComponents = z.TypeOf<typeof componentsSchema>;

export const appByVersionSchema = z.object({
  appId: z.string(),
  data: z.object({
    components: componentsSchema,
  }),
});

export const appVersionSchema = z.object({
  appVersions: z.array(
    z.object({
      /** Application ID */
      id: z.string(),
      fullVersion: z.string().optional().default('0.0.0'),
      /** indicates if this is the draft instance of the version. */
      state: z.string().optional(),
      /** status of the app */
      status: z.string().optional(),
    })
  ),
});

const appSecretsSchema = z.object({
  appSecret: z.string(),
  webhookPublicKey: z.string(),
});

export type AppSecrets = z.TypeOf<typeof appSecretsSchema>;

export const appSchema = z.object({
  app: z.object({
    appSecrets: appSecretsSchema,
  }),
});

export const createDevelopmentSiteSchema = z.object({
  developmentSite: z.object({
    id: z.string(),
    siteName: z.string(),
  }),
});

export const getDevelopmentSitesSchema = z.object({
  developmentSites: z.array(z.object({})),
});

export const getDevelopmentSitesLimitSchema = z.object({
  limit: z.number(),
});

const developerAppSchema = z.object({
  id: z.string(),
  appData: z.object({
    appName: z.string(),
  }),
});

export const getDeveloperAppsSchema = z.object({
  myApps: z.array(developerAppSchema),
});

export type DeveloperApp = z.TypeOf<typeof developerAppSchema>;

const requiredApp = z.object({ id: z.string(), name: z.string() });

export type App = z.TypeOf<typeof requiredApp>;

export const queryAppSchema = z.object({
  apps: z.array(requiredApp),
});

export const marketListingSchema = z.object({
  id: z.string(),
  appId: z.string(),
  revision: z.string(),
  languageCode: z.string(),
  installationRequirement: z
    .object({
      requiredApps: z.array(
        z.object({
          id: z.string(),
          slug: z.string(),
        })
      ),
    })
    .optional(),
});

export const managedAppsSchema = z.object({
  managedApps: z.array(
    z.object({
      appId: z.string(),
      installationDetails: z.object({
        group: z.nativeEnum(AppGroup),
      }),
    })
  ),
});

const cliCLoneDataSchema = z.object({
  repositoryUrl: z.string(),
  pathInRepository: z.string().optional(),
});

const appTemplateSchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string(),
  developmentTools: z.array(z.nativeEnum(DevelopmentTool)),
  cliCloneData: cliCLoneDataSchema.optional(),
});

export const queryAppTemplateSchema = z.object({
  appTemplates: z.array(appTemplateSchema),
});

export const cliAppTemplateSchema = appTemplateSchema.extend({
  cliCloneData: cliCLoneDataSchema,
});

export type CliAppTemplate = z.TypeOf<typeof cliAppTemplateSchema>;

export {
  ComponentType,
  MonitoringType,
} from '@wix/ambassador-devcenter-apps-v1-app-old/types';
export { DevelopmentTool } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
